<template>
  <div class="optimize-overlay" @click.self="onCancel">
    <div class="optimize-modal">
      <div class="modal-header">
        <div class="title-left">
          <span class="title-icon">🧠</span>
          <span class="title-text">智能优化向导</span>
        </div>
        <button class="close-btn" @click="onCancel">×</button>
      </div>

      <div class="modal-body">
        <!-- 步骤指示器 -->
        <div class="steps-indicator">
          <div :class="['step', { active: currentStep === 1, completed: currentStep > 1 }]">
            <div class="step-number">1</div>
            <div class="step-title">选择产品</div>
          </div>
          <div :class="['step', { active: currentStep === 2, completed: currentStep > 2 }]">
            <div class="step-number">2</div>
            <div class="step-title">选择优化目标</div>
          </div>
          <div :class="['step', { active: currentStep === 3, completed: currentStep > 3 }]">
            <div class="step-number">3</div>
            <div class="step-title">{{ target === 'competitor' ? '选择竞品' : '生成报告' }}</div>
          </div>
        </div>

        <!-- 步骤1：选择本品对象（车系/车型，单选） -->
        <div v-if="currentStep === 1" class="step-content">
          <div class="section">
            <div class="section-header">
              <span class="section-title">选择产品对象</span>
              <div class="mode-switch">
                <button :class="['mode-btn', { active: mode === 'series' }]" @click="switchMode('series')">按车系</button>
                <button :class="['mode-btn', { active: mode === 'car' }]" @click="switchMode('car')">按车型</button>
              </div>
            </div>
            <div class="selector-list">
              <div v-for="opt in currentOptions" :key="opt.value" class="selector-item" @click="selectProduct(opt.value)">
                <label>
                  <input type="radio" :value="opt.value" v-model="selectedObject" />
                  <span class="obj-name">{{ opt.value }}</span>
                  <span class="obj-count">{{ opt.count }}条口碑</span>
                </label>
              </div>
            </div>
            <div class="step-actions">
              <button class="btn-next" @click="nextStep" :disabled="!selectedObject">下一步</button>
            </div>
          </div>
        </div>

        <!-- 步骤2：选择优化目标 -->
        <div v-if="currentStep === 2" class="step-content">
          <div class="section">
            <div class="section-header">
              <span class="section-title">选择优化目标</span>
            </div>
            <div class="targets-grid">
              <label class="target-item" @click="selectTarget('sentiment')">
                <input type="radio" value="sentiment" v-model="target" />
                <div class="target-content">
                  <div class="target-title">按口碑点优化</div>
                  <div class="target-desc">基于负面口碑点，优化产品体验</div>
                </div>
              </label>
              <label class="target-item" @click="selectTarget('competitor')">
                <input type="radio" value="competitor" v-model="target" />
                <div class="target-content">
                  <div class="target-title">对比竞争对手优化</div>
                  <div class="target-desc">分析竞品优势，制定针对性策略</div>
                </div>
              </label>
              <label class="target-item" @click="selectTarget('ai')">
                <input type="radio" value="ai" v-model="target" />
                <div class="target-content">
                  <div class="target-title">AI智能优化</div>
                  <div class="target-desc">综合口碑点和竞品分析，智能推荐策略</div>
                </div>
              </label>
            </div>
            <div class="step-actions">
              <button class="btn-prev" @click="prevStep">上一步</button>
              <button class="btn-next" @click="nextStep" :disabled="!target">下一步</button>
            </div>
          </div>
        </div>

        <!-- 步骤3：选择竞品（仅在目标为competitor时显示） -->
        <div v-if="currentStep === 3 && target === 'competitor'" class="step-content">
          <div class="section">
            <div class="section-header">
              <span class="section-title">选择竞品对象</span>
              <div class="mode-switch">
                <button :class="['mode-btn', { active: compMode === 'series' }]" @click="compMode = 'series'">按车系</button>
                <button :class="['mode-btn', { active: compMode === 'car' }]" @click="compMode = 'car'">按车型</button>
              </div>
            </div>
            <div class="competitor-preview" v-if="competitorPreviewData">
              <div class="preview-title">竞品数据预览</div>
              <div class="preview-stats">
                <div class="stat-item">
                  <div class="stat-label">口碑总数</div>
                  <div class="stat-value">{{ competitorPreviewData.totalCount }}</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">好评率</div>
                  <div class="stat-value">{{ (competitorPreviewData.positiveRate * 100).toFixed(1) }}%</div>
                </div>
                <div class="stat-item">
                  <div class="stat-label">差评率</div>
                  <div class="stat-value">{{ (competitorPreviewData.negativeRate * 100).toFixed(1) }}%</div>
                </div>
              </div>
            </div>
            <div class="selector-list">
              <div v-for="opt in competitorOptions" :key="opt.value" class="selector-item" @click="toggleCompetitor(opt.value)">
                <label>
                  <input type="checkbox" :value="opt.value" v-model="selectedCompetitors" />
                  <span class="obj-name">{{ opt.value }}</span>
                  <span class="obj-count">{{ opt.count }}条口碑</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- 最终步骤：生成报告（统一到第3步） -->
        <div v-if="currentStep === 3" class="step-content">
          <div class="section">
            <div class="section-header">
              <span class="section-title">生成报告</span>
            </div>
            
            <!-- 口碑点优化：直接显示Top口碑点 -->
            <div v-if="target === 'sentiment' && topAspects.length > 0" class="top-aspects-section">
              <div class="section-title">Top口碑点（点击选择要优化的口碑点）</div>
              <div class="sort-controls">
                <span class="sort-label">排序：</span>
                <button class="sort-btn" :class="{ active: sortKey === 'priority' }" @click="sortKey = 'priority'">按优先级</button>
                <button class="sort-btn" :class="{ active: sortKey === 'negRate' }" @click="sortKey = 'negRate'">按负面率</button>
                <button class="sort-btn" :class="{ active: sortKey === 'coverage' }" @click="sortKey = 'coverage'">按覆盖度</button>
              </div>
              <div class="top-grid">
                <div v-for="item in topAspectsView" :key="item.aspect" class="top-item" @click="toggleAspect(item.aspect)">
                  <label>
                    <input type="checkbox" :value="item.aspect" v-model="selectedAspects" />
                    <span class="top-name">{{ item.aspect }}</span>
                    <span class="top-metrics">
                      覆盖: {{ (item.coverage * 100).toFixed(1) }}%
                      · 负面: {{ (item.negRate * 100).toFixed(1) }}%
                      · 优先级: {{ (item.priorityScore * 100).toFixed(1) }}
                    </span>
                    <span class="top-meta">总计:{{ item.total }} 负面:{{ item.neg }}</span>
                    <span v-if="isRecommended(item)" class="recommend-badge">推荐</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- 第一印象与示例（卡片式，三列布局） -->
            <div v-if="(selectedAspects && selectedAspects.length) || isLoadingInsights" class="insights-section">
              <div class="section-title">第一印象与示例（所选口碑点）</div>
              <div v-if="isLoadingInsights" class="loading-tip">数据加载中...</div>
              <div v-else class="insight-cards">
                <div v-for="asp in selectedAspects" :key="asp" class="insight-card">
                  <div class="insight-card-header">
                    <div class="insight-title">📌 {{ asp }}</div>
                    <div class="insight-metrics" v-if="getAspectMetrics(asp)">
                      <span class="metric">覆盖 {{ (getAspectMetrics(asp).coverage * 100).toFixed(1) }}%</span>
                      <span class="metric">负面 {{ (getAspectMetrics(asp).negRate * 100).toFixed(1) }}%</span>
                      <span class="metric">优先级 {{ (calcPriority(getAspectMetrics(asp)) * 100).toFixed(1) }}</span>
                    </div>
                  </div>
                  <!-- 词云：只保留图表展示，不显示词标签/高亮片 -->
                  <div v-if="aspectInsights[asp] && aspectInsights[asp].wordCloud && aspectInsights[asp].wordCloud.length">
                    <div class="cloud-chart" :ref="el => registerWordCloudRef(asp, el)"></div>
                  </div>
                  <!-- 示例（改为三行上下排列：正面/负面/AI） -->
                  <div class="examples-stack" v-if="aspectInsights[asp] && aspectInsights[asp].examples">
                    <div v-if="aspectInsights[asp].examples.positive.length === 0" class="example-empty">正面：暂无</div>
                    <div v-else class="example-line positive">正面：{{ formatExampleLine(aspectInsights[asp].examples.positive) }}</div>
                    <div v-if="aspectInsights[asp].examples.negative.length === 0" class="example-empty">负面：暂无</div>
                    <div v-else class="example-line negative">负面：{{ formatExampleLine(aspectInsights[asp].examples.negative) }}</div>
                  </div>
                  <!-- AI一行分析 -->
                  <div class="ai-one-line" v-if="aspectInsights[asp] && aspectInsights[asp].aiLine">AI分析：{{ aspectInsights[asp].aiLine }}</div>
                  <div class="ai-one-line" v-else-if="isLoadingInsights">AI分析：生成中...</div>
                  <!-- 竞品第一印象（若选择竞品） -->
                  <div v-if="aspectInsights.__competitor__ && aspectInsights.__competitor__[asp]" class="competitor-insight">
                    <div class="competitor-title">对比：竞品第一印象</div>
                    <!-- 竞品区域不再显示词标签，仅保留示例；如需词云图可后续接入 -->
                    <div class="examples-row" v-if="aspectInsights.__competitor__[asp].examples">
                      <div class="example-col">
                        <div class="example-title">正面示例（竞品）</div>
                        <div v-if="aspectInsights.__competitor__[asp].examples.positive.length === 0" class="example-empty">暂无</div>
                        <div v-else class="example-line positive">正面：{{ formatExampleLine(aspectInsights.__competitor__[asp].examples.positive) }}</div>
                      </div>
                      <div class="example-col">
                        <div class="example-title">负面示例（竞品）</div>
                        <div v-if="aspectInsights.__competitor__[asp].examples.negative.length === 0" class="example-empty">暂无</div>
                        <div v-else class="example-line negative">负面：{{ formatExampleLine(aspectInsights.__competitor__[asp].examples.negative) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 已移除外层AI分析，AI一行在每个方面卡片内展示 -->
            
            <div class="step-actions">
              <button class="btn-prev" @click="prevStep">上一步</button>
              <button class="btn-confirm" @click="onConfirm" :disabled="isGenerating">生成报告</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dongchediApi from '../api/dongchediApi'
import { getTopAspects, prepareContext } from '../services/optimizationService'
import aiApi from '../api/aiApi'
import * as echarts from 'echarts'
import 'echarts-wordcloud'

// 确保词云插件正确注册
console.log('ECharts版本:', echarts.version)
console.log('词云插件是否可用:', echarts.getMap ? '是' : '否')

export default {
  name: 'OptimizationWizard',
  props: {
    visible: Boolean,
    baseFilters: Object
  },
  data() {
    return {
      currentStep: 1,
      mode: 'series', // 'series' | 'car'
      compMode: 'series', // 'series' | 'car'
      selectedObject: '', // 单选
      selectedCompetitors: [], // 多选
      target: '', // 'sentiment' | 'competitor' | 'ai'
      topAspects: [],
      selectedAspects: [],
      strategyPool: [],
      isGenerating: false,
      competitorPreviewData: null,
      sortKey: 'priority',
      recommendationThresholds: { priority: 0.6, negRate: 0.25, coverage: 0.10 },
      // 第一印象/示例/词云
      aspectInsights: {},
      isLoadingInsights: false,
      aiInsightsMarkdown: '',
      aiLoading: false,
      insightDebounceTimer: null,
      // 词云图表引用与实例
      wordCloudRefs: {},
      wordCloudCharts: {}
    }
  },
  computed: {
    ...mapState(['seriesOptions', 'carOptions']),
    currentOptions() {
      return this.mode === 'series' ? this.seriesOptions : this.carOptions
    },
    competitorOptions() {
      return this.compMode === 'series' ? this.seriesOptions : this.carOptions
    },
    topAspectsView() {
      const list = (this.topAspects || []).map(item => ({
        ...item,
        priorityScore: this.calcPriority(item)
      }))
      const key = this.sortKey
      return list.sort((a, b) => {
        const va = key === 'priority' ? a.priorityScore : key === 'negRate' ? a.negRate : a.coverage
        const vb = key === 'priority' ? b.priorityScore : key === 'negRate' ? b.negRate : b.coverage
        return vb - va
      })
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetWizard()
        // 检查Vuex store中是否有数据，如果没有则触发父组件加载数据
        if (this.seriesOptions.length === 0 || this.carOptions.length === 0) {
          this.$emit('need-load-data')
        }
      }
    },
    target(newVal) {
      if (newVal === 'sentiment' && this.selectedObject) {
        this.loadTopAspects()
      }
    },
    selectedObject(newVal) {
      if (newVal && this.target === 'sentiment') {
        this.loadTopAspects()
      }
    }
    ,
    selectedAspects(newVal) {
      if (this.insightDebounceTimer) {
        clearTimeout(this.insightDebounceTimer)
      }
      // 轻微防抖，减少重复请求
      this.insightDebounceTimer = setTimeout(() => {
        if (Array.isArray(newVal) && newVal.length > 0) {
          this.loadAspectInsights()
        } else {
          this.aspectInsights = {}
        }
      }, 300)
    }
  },
  methods: {
    // 注册词云容器引用
    registerWordCloudRef(asp, el) {
      if (el) {
        console.log('注册词云容器:', asp, el)
        this.$set(this.wordCloudRefs, asp, el)
        // 容器就绪后再触发渲染，避免在DOM尚未挂载时渲染失败
        this.$nextTick(() => {
          this.renderWordCloudChart(asp)
        })
      }
    },
    // 提取意见文本（去掉“aspect:”前缀）
    opinionText(term) {
      if (!term) return ''
      const parts = String(term).split(':')
      return parts.length > 1 ? parts.slice(1).join(':') : term
    },
    // 计算高亮文案：优先展示“确实无法使用”，否则展示负面最高词
    getCloudHighlight(asp) {
      const wc = (this.aspectInsights[asp] && this.aspectInsights[asp].wordCloud) || []
      const hit = wc.find(w => (w.sentiment === 'negative') && (String(w.term).includes('无法使用') || String(w.term).includes('不能使用') || String(w.term).includes('不可用')))
      if (hit) return '确实无法使用'
      const topNeg = wc.filter(w => w.sentiment === 'negative').sort((a, b) => b.count - a.count)[0]
      return topNeg ? this.opinionText(topNeg.term) : ''
    },
    // 渲染词云图表
    renderWordCloudChart(asp) {
      console.log('渲染词云图表:', asp)
      const container = this.wordCloudRefs[asp]
      const wc = (this.aspectInsights[asp] && this.aspectInsights[asp].wordCloud) || []
      console.log('容器:', container, '词云数据:', wc)
      
      if (!container) {
        console.warn('词云容器未找到:', asp)
        return
      }
      if (!wc.length) {
        console.warn('词云数据为空:', asp)
        // 显示占位符
        container.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af;">暂无词云数据</div>'
        return
      }
      
      if (this.wordCloudCharts[asp]) {
        this.wordCloudCharts[asp].dispose()
      }
      
      try {
        const chart = echarts.init(container)
        this.wordCloudCharts[asp] = chart
        const data = wc.map(item => {
          const name = this.opinionText(item.term)
          const sentiment = item.sentiment || 'neutral'
          const color = sentiment === 'negative' ? '#f87171' : sentiment === 'positive' ? '#34d399' : '#9ca3af'
          return { name, value: Math.round(item.count), textStyle: { color } }
        })
        console.log('词云图表数据:', data)
        
        // 先尝试简单的柱状图测试ECharts是否正常工作
         const isWordCloudAvailable = echarts.getModel && echarts.getModel().getSeriesByType
         console.log('词云系列是否可用:', isWordCloudAvailable)
         
         if (data.length > 0) {
           chart.setOption({
             tooltip: {
               trigger: 'item',
               formatter: '{b}: {c}'
             },
             series: [{
               type: 'wordCloud',
               shape: 'circle',
               left: 'center',
               top: 'center',
               width: '100%',
               height: '100%',
               right: null,
               bottom: null,
               sizeRange: [12, 40],
               rotationRange: [-90, 90],
               rotationStep: 45,
               gridSize: 8,
               drawOutOfBound: false,
               layoutAnimation: true,
               textStyle: {
                 fontFamily: 'sans-serif',
                 fontWeight: 'bold'
               },
               emphasis: {
                 focus: 'self',
                 textStyle: {
                   shadowBlur: 10,
                   shadowColor: '#333'
                 }
               },
               data: data
             }]
           })
         } else {
           // 如果没有数据，显示一个简单的提示图表
           chart.setOption({
             title: {
               text: '暂无数据',
               left: 'center',
               top: 'center',
               textStyle: {
                 color: '#999',
                 fontSize: 14
               }
             }
           })
         }
        console.log('词云图表渲染完成:', asp)
      } catch (error) {
        console.error('词云图表渲染失败:', error)
        container.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#ef4444;">词云渲染失败</div>'
      }
    },
    resetWizard() {
      this.currentStep = 1
      this.mode = 'series'
      this.compMode = 'series'
      this.selectedObject = ''
      this.selectedCompetitors = []
      this.target = ''
      this.topAspects = []
      this.selectedAspects = []
      this.strategyPool = []
      this.isGenerating = false
      this.competitorPreviewData = null
    },
    switchMode(newMode) {
      this.mode = newMode
      this.selectedObject = ''
    },
    selectProduct(value) {
      this.selectedObject = value
    },
    selectTarget(value) {
      this.target = value
    },
    toggleCompetitor(value) {
      const index = this.selectedCompetitors.indexOf(value)
      if (index > -1) {
        this.selectedCompetitors.splice(index, 1)
      } else {
        this.selectedCompetitors.push(value)
        this.loadCompetitorPreview(value)
      }
    },
    toggleAspect(value) {
      const index = this.selectedAspects.indexOf(value)
      if (index > -1) {
        this.selectedAspects.splice(index, 1)
      } else {
        this.selectedAspects.push(value)
      }
    },
    async loadCompetitorPreview(competitor) {
      try {
        const filters = { ...(this.baseFilters || {}) }
        if (this.compMode === 'series') {
          filters.seriesName = competitor
        } else {
          filters.carName = competitor
        }
        
        const [stats, sentimentDist] = await Promise.all([
          dongchediApi.getSentimentStats(filters),
          dongchediApi.getSentimentDistribution(filters)
        ])
        
        const totalCount = stats.total_count || 0
        const positiveCount = sentimentDist.find(d => d.sentiment === 'positive')?.count || 0
        const negativeCount = sentimentDist.find(d => d.sentiment === 'negative')?.count || 0
        
        this.competitorPreviewData = {
          totalCount,
          positiveRate: totalCount > 0 ? positiveCount / totalCount : 0,
          negativeRate: totalCount > 0 ? negativeCount / totalCount : 0
        }
      } catch (error) {
        console.error('加载竞品预览数据失败:', error)
      }
    },
    async loadTopAspects() {
      if (!this.selectedObject || this.target !== 'sentiment') return
      
      try {
        this.isGenerating = true
        const filters = { ...(this.baseFilters || {}) }
        if (this.mode === 'series') {
          filters.seriesName = this.selectedObject
        } else {
          filters.carName = this.selectedObject
        }
        
        const topAspects = await getTopAspects({
          mode: this.mode,
          selectedObjects: [this.selectedObject],
          baseFilters: filters
        })
        
        this.topAspects = topAspects
      } catch (error) {
        console.error('加载Top口碑点失败:', error)
        alert('加载Top口碑点失败')
      } finally {
        this.isGenerating = false
      }
    },
    calcPriority(item) {
      const posRate = Number(item.posRate || 0)
      const negRate = Number(item.negRate || 0)
      const coverage = Number(item.coverage || 0)
      let score = 0.4 * negRate + 0.4 * coverage + 0.2 * (1 - posRate)
      if (!Number.isFinite(score)) score = 0
      score = Math.max(0, Math.min(1, score))
      return score
    },
    isRecommended(item) {
      const score = item.priorityScore != null ? item.priorityScore : this.calcPriority(item)
      const t = this.recommendationThresholds
      return score >= t.priority || Number(item.negRate || 0) >= t.negRate || Number(item.coverage || 0) >= t.coverage
    },
    getAspectMetrics(asp) {
      return (this.topAspects || []).find(a => a.aspect === asp) || null
    },
    sentimentIcon(s) {
      if (s === 'positive') return '✓'
      if (s === 'negative') return '×'
      return '•'
    },
    buildBaseFilters() {
      const filters = { ...(this.baseFilters || {}) }
      if (this.mode === 'series') filters.seriesName = this.selectedObject
      else filters.carName = this.selectedObject
      return filters
    },
    
    formatExampleLine(items = [], limit = 2) {
      const texts = (items || []).slice(0, limit).map(i => (i && i.text) ? String(i.text).trim() : '').filter(Boolean)
      if (!texts.length) return '暂无'
      return texts.join(' ｜ ')
    },
    computeWordCloud(rows = [], aspect) {
      const map = new Map()
      for (const item of rows || []) {
        let arr = []
        try {
          if (item.opinion_analysis) {
            if (Array.isArray(item.opinion_analysis)) arr = item.opinion_analysis
            else if (typeof item.opinion_analysis === 'string') arr = JSON.parse(item.opinion_analysis)
          }
        } catch (e) {
          console.warn('解析opinion_analysis失败:', e)
          arr = []
        }
        for (const op of arr) {
          if (!op || !op.aspect || !op.opinion) continue
          if (aspect && op.aspect !== aspect) continue
          const term = `${op.aspect}:${op.opinion}`
          const weight = Math.max(1, Math.abs(parseFloat(op.score || 0)) / 20)
          const key = `${term}|${op.sentiment || 'neutral'}`
          const prev = map.get(key) || { count: 0, sentiment: (op.sentiment || 'neutral') }
          prev.count += weight
          map.set(key, prev)
        }
      }
      return Array.from(map.entries()).map(([key, entry]) => {
        const [term] = key.split('|')
        return { term, count: entry.count, sentiment: entry.sentiment }
      }).sort((a, b) => b.count - a.count).slice(0, 30)
    },
    async loadAspectInsights() {
      try {
        this.isLoadingInsights = true
        const filters = this.buildBaseFilters()

        const insights = {}
        for (const aspect of this.selectedAspects || []) {
          const rows = await dongchediApi.getAspectAnalysis({ ...filters, keyword: aspect })
          const wc = this.computeWordCloud(rows, aspect)
          const examples = this.pickExamples(rows)
          let aiLine = ''
          try {
            const prompt = aiApi.generateOneLineInsightPrompt({
              aspect,
              object: this.selectedObject,
              mode: this.mode,
              target: this.target,
              wordCloud: wc.slice(0, 6),
              examples: {
                positive: (examples.positive || []).map(e => e.text).slice(0, 2),
                negative: (examples.negative || []).map(e => e.text).slice(0, 2)
              }
            })
            aiLine = await aiApi.analyzeData(prompt)
          } catch (e) {
            console.warn('生成AI一行失败:', e)
            aiLine = '分析不可用'
          }
          insights[aspect] = { wordCloud: wc, examples, aiLine }
        }
        // 竞品汇总（若选择竞品）
        if (this.target === 'competitor' && (this.selectedCompetitors || []).length) {
          const compInsights = {}
          for (const aspect of this.selectedAspects || []) {
            const compRows = []
            for (const name of this.selectedCompetitors) {
              const cf = { ...(this.baseFilters || {}) }
              if (this.compMode === 'series') cf.seriesName = name
              else cf.carName = name
              const r = await dongchediApi.getAspectAnalysis({ ...cf, keyword: aspect })
              compRows.push(...(r || []))
            }
            compInsights[aspect] = {
              wordCloud: this.computeWordCloud(compRows, aspect),
              examples: this.pickExamples(compRows)
            }
          }
          this.aspectInsights = { ...insights, __competitor__: compInsights }
        } else {
          this.aspectInsights = insights
        }
        // 渲染所选方面的词云图
        this.$nextTick(() => {
          (this.selectedAspects || []).forEach(a => this.renderWordCloudChart(a))
        })
      } catch (e) {
        console.error('加载第一印象与示例失败:', e)
        alert('加载第一印象与示例失败')
      } finally {
        this.isLoadingInsights = false
      }
    },
    pickExamples(rows = []) {
      const normalize = v => {
        const n = parseFloat(v)
        return Number.isFinite(n) ? n : 0
      }
      const positives = (rows || []).filter(r => r.sentiment === 'positive')
        .sort((a, b) => normalize(b.sentiment_score) - normalize(a.sentiment_score))
        .slice(0, 3)
        .map(r => ({ text: r.original_sentence, score: normalize(r.sentiment_score) }))
      const negatives = (rows || []).filter(r => r.sentiment === 'negative')
        .sort((a, b) => normalize(a.sentiment_score) - normalize(b.sentiment_score))
        .slice(0, 3)
        .map(r => ({ text: r.original_sentence, score: normalize(r.sentiment_score) }))
      return { positive: positives, negative: negatives }
    },
    async loadAIInsights() {
      try {
        this.aiLoading = true
        // 构建上下文并生成单行AI分析（≤20字）
        const payload = {
          mode: this.mode,
          selectedObjects: [this.selectedObject],
          target: this.target || 'sentiment',
          competitors: { mode: this.compMode, selectedObjects: this.selectedCompetitors },
          strategyPool: this.strategyPool,
          selectedAspects: this.selectedAspects
        }
        const ctx = await prepareContext(payload, this.baseFilters || {}, 'PM', '产品经理')
        const shortPrompt = `请基于以下JSON输出一句中文单行结论（不要换行），分析当前选定对象的口碑第一印象；严格限制不超过30个字：\n\n${JSON.stringify({
          object: this.selectedObject,
          mode: this.mode,
          target: this.target,
          selectedAspects: this.selectedAspects,
          wordCloud: (ctx.wordCloud || []).slice(0, 8)
        })}`
        const oneLine = await aiApi.analyzeData(shortPrompt)
        this.aiInsightsMarkdown = (oneLine || '').trim()
      } catch (e) {
        console.error('AI分析失败:', e)
        this.aiInsightsMarkdown = '（AI分析生成失败）'
      } finally {
        this.aiLoading = false
      }
    },
    nextStep() {
      if (this.currentStep === 2 && this.target !== 'competitor') {
        // 跳过竞品选择步骤，直接到第3步
        this.currentStep = 3
      } else if (this.currentStep < 3) {
        this.currentStep++
      }
      
      // 如果是口碑点优化，进入第3步时自动加载Top口碑点
      if (this.currentStep === 3 && this.target === 'sentiment') {
        this.loadTopAspects()
      }
    },
    prevStep() {
      if (this.currentStep === 3 && this.target !== 'competitor') {
        // 口碑点/AI目标从第3步返回到第2步
        this.currentStep = 2
      } else if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    onCancel() {
      this.$emit('cancel')
    },
    onConfirm() {
      // 构建卡片指标映射（仅对已选口碑点）
      const aspectMetrics = {}
      for (const asp of this.selectedAspects || []) {
        const m = this.getAspectMetrics(asp)
        if (m) aspectMetrics[asp] = {
          posRate: m.posRate,
          negRate: m.negRate,
          coverage: m.coverage,
          total: m.total,
          pos: m.pos,
          neg: m.neg,
          neu: m.neu
        }
      }

      const payload = {
        mode: this.mode,
        selectedObjects: [this.selectedObject],
        target: this.target,
        competitors: { mode: this.compMode, selectedObjects: this.selectedCompetitors },
        selectedAspects: this.selectedAspects,
        // 报告需要的卡片数据（第一印象/示例/AI一行）
        aspectInsights: this.aspectInsights,
        aspectMetrics,
        filters: this.baseFilters
      }
      this.$emit('confirm', payload)
    }
  }
}
</script>

<style scoped>
.optimize-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 10001; }
.optimize-modal { width: 90vw; max-width: 1100px; max-height: 85vh; background: #fff; border-radius: 16px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); display: flex; flex-direction: column; overflow: hidden; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; }
.title-left { display: flex; align-items: center; gap: 10px; font-weight: 700; }
.close-btn { width: 28px; height: 28px; border: none; background: rgba(255,255,255,0.2); color: white; border-radius: 50%; cursor: pointer; }
.modal-body { padding: 16px 20px; overflow-y: auto; }

/* 步骤指示器 */
.steps-indicator { display: flex; justify-content: space-between; margin-bottom: 24px; padding: 0 20px; }
.step { display: flex; flex-direction: column; align-items: center; flex: 1; position: relative; }
.step:not(:last-child)::after { content: ''; position: absolute; top: 20px; left: 50%; width: 100%; height: 2px; background: #e2e8f0; z-index: 1; }
.step.completed:not(:last-child)::after { background: #22c55e; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: #e2e8f0; color: #718096; display: flex; align-items: center; justify-content: center; font-weight: 700; margin-bottom: 8px; position: relative; z-index: 2; }
.step.active .step-number { background: #5b6df2; color: white; }
.step.completed .step-number { background: #22c55e; color: white; }
.step-title { font-size: 14px; color: #718096; text-align: center; }
.step.active .step-title { color: #5b6df2; font-weight: 600; }
.step.completed .step-title { color: #22c55e; font-weight: 600; }

/* 步骤内容 */
.step-content { min-height: 400px; }
.section { margin-bottom: 16px; }
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.section-title { font-weight: 700; color: #2d3748; font-size: 18px; }
.mode-switch { display: flex; gap: 10px; }
.mode-btn { padding: 8px 14px; border-radius: 8px; border: 1px solid #e2e8f0; background: #f7fafc; cursor: pointer; font-weight: 600; }
.mode-btn.active { background: #eef2ff; border-color: #cbd5e0; color: #5b6df2; }
.selector-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; margin-bottom: 20px; max-height: 300px; overflow-y: auto; }
.selector-item { background: #fafbff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; cursor: pointer; transition: all 0.2s; }
.selector-item:hover { background: #f0f4ff; border-color: #cbd5e0; }
.selector-item.selected { background: #eef2ff; border-color: #5b6df2; }
.obj-name { margin-left: 8px; font-weight: 600; color: #2d3748; }
.obj-count { margin-left: auto; color: #718096; font-size: 12px; }

/* 竞品预览 */
.competitor-preview { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
.preview-title { font-weight: 600; color: #2d3748; margin-bottom: 12px; }
.preview-stats { display: flex; gap: 16px; }
.stat-item { text-align: center; }
.stat-label { font-size: 12px; color: #718096; margin-bottom: 4px; }
.stat-value { font-size: 18px; font-weight: 700; color: #2d3748; }

/* 目标选择 */
.targets-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; margin-bottom: 20px; }
.target-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; cursor: pointer; transition: all 0.2s; display: flex; align-items: flex-start; gap: 12px; }
.target-item:hover { background: #f0f4ff; border-color: #cbd5e0; }
.target-item.selected { background: #eef2ff; border-color: #5b6df2; }
.target-content { flex: 1; }
.target-title { font-weight: 600; color: #2d3748; margin-bottom: 4px; }
.target-desc { font-size: 14px; color: #718096; }

/* Top口碑点 */
.top-aspects-section { margin-bottom: 20px; }
.top-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px; margin-top: 8px; max-height: 300px; overflow-y: auto; }
.top-item { display: flex; align-items: center; gap: 8px; padding: 8px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; transition: all 0.2s; }
.top-item:hover { background: #f8fafc; }
.top-item.selected { background: #eef2ff; border-color: #5b6df2; }
.top-name { font-weight: 600; color: #1f2937; }
.top-metrics { margin-left: auto; color: #334155; font-size: 12px; font-weight: 600; }
.top-meta { color: #718096; font-size: 12px; margin-left: 8px; }
.recommend-badge { margin-left: 8px; padding: 2px 6px; border-radius: 10px; background: #dcfce7; color: #166534; font-size: 12px; font-weight: 700; }
.sort-controls { display: flex; align-items: center; gap: 8px; margin: 8px 0; }
.sort-label { color: #718096; font-size: 12px; }
.sort-btn { padding: 4px 10px; border-radius: 999px; border: 1px solid #e2e8f0; background: #f7fafc; cursor: pointer; font-size: 12px; }
.sort-btn.active { background: #eef2ff; border-color: #cbd5e0; color: #5b6df2; }

/* 策略池 */
.strategy-pool-section { margin-bottom: 20px; }
.pool-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 8px; max-height: 300px; overflow-y: auto; }
.pool-item { padding: 12px; border-radius: 8px; border: 1px solid #edf2f7; background: #fff; }
.pool-aspect { font-weight: 700; color: #1f2937; margin-bottom: 8px; }
.pool-stats { display: flex; flex-wrap: wrap; gap: 8px; }
.pool-stats span { font-size: 12px; color: #718096; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }

/* 步骤操作 */
.step-actions { display: flex; justify-content: space-between; margin-top: 20px; }
.btn-prev, .btn-next, .btn-confirm, .btn-generate { padding: 10px 16px; border-radius: 8px; cursor: pointer; font-weight: 600; border: none; }
.btn-prev { background: #edf2f7; color: #4a5568; }
.btn-next { background: linear-gradient(135deg, #5b6df2 0%, #7b5cff 100%); color: #fff; }
.btn-next:disabled { background: #cbd5e0; cursor: not-allowed; }
.btn-confirm { background: linear-gradient(135deg, #22c55e 0%, #10b981 100%); color: #fff; }
.btn-confirm:disabled { background: #cbd5e0; cursor: not-allowed; }
.btn-generate { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #fff; }
.btn-generate:disabled { background: #cbd5e0; cursor: not-allowed; }
/* --- 第一印象与示例：卡片与词云高级样式 --- */
.insights-section .section-title { font-size: 16px; font-weight: 700; color: #111827; margin: 12px 0; }
.insight-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.insight-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 6px 16px rgba(0,0,0,0.08); padding: 12px 14px; display: flex; flex-direction: column; }
.insight-card-header { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
.insight-title { font-weight: 600; font-size: 14px; color: #111827; }
.insight-metrics .metric { margin-left: 8px; font-size: 12px; color: #6b7280; background: #f3f4f6; border-radius: 8px; padding: 4px 8px; }
.cloud-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.cloud-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 999px; line-height: 1; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.06); transition: transform 0.2s ease; }
.cloud-chip:hover { transform: translateY(-1px); }
.cloud-chip .chip-icon { font-size: 12px; opacity: 0.9; }
.cloud-chip .chip-text { font-weight: 500; letter-spacing: 0.1px; }
.cloud-chip.positive { color: #065f46; background: linear-gradient(180deg, #d1fae5 0%, #a7f3d0 100%); }
.cloud-chip.negative { color: #7f1d1d; background: linear-gradient(180deg, #fee2e2 0%, #fecaca 100%); }
.cloud-chip.neutral { color: #374151; background: linear-gradient(180deg, #e5e7eb 0%, #d1d5db 100%); }
.cloud-chip.alt { filter: saturate(115%); }
.examples-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.example-col { background: #fafafa; border: 1px solid #eee; border-radius: 10px; padding: 10px; }
.example-title { font-weight: 600; font-size: 12px; color: #374151; margin-bottom: 6px; }
.example-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.example-list li { font-size: 12px; color: #111827; background: #fff; border: 1px solid #f3f4f6; border-radius: 8px; padding: 8px 10px; box-shadow: 0 2px 6px rgba(0,0,0,0.04); overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.example-empty { font-size: 12px; color: #6b7280; }
.competitor-insight { margin-top: 10px; border-top: 1px dashed #e5e7eb; padding-top: 10px; }
.competitor-title { font-weight: 600; font-size: 12px; color: #4b5563; margin-bottom: 6px; }
/* 竖向三行示例容器 */
.examples-stack { display: flex; flex-direction: column; gap: 6px; margin-bottom: 6px; }
/* 高亮与图表容器 */
.cloud-highlight { font-size: 20px; font-weight: 800; color: #7f1d1d; background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; padding: 8px 12px; display: inline-block; margin-bottom: 8px; }
.cloud-chart { width: 100%; height: 160px; border: 1px dashed #e5e7eb; border-radius: 10px; background: #fff; }
/* 三行外框与配色（正面/负面/AI） */
.example-line { font-size: 12px; line-height: 1.6; white-space: normal; word-break: break-word; overflow-wrap: anywhere; word-wrap: break-word; border-radius: 10px; padding: 10px 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); max-width: 100%; }
.example-line.positive { color: #064e3b; background: #ecfdf5; border: 1px solid #10b981; border-left-width: 4px; }
.example-line.negative { color: #7f1d1d; background: #fef2f2; border: 1px solid #ef4444; border-left-width: 4px; }
.ai-one-line { margin-top: 8px; font-size: 12px; line-height: 1.6; white-space: normal; word-break: break-word; overflow-wrap: anywhere; word-wrap: break-word; color: #1e3a8a; background: #eff6ff; border: 1px solid #60a5fa; border-left-width: 4px; border-radius: 10px; padding: 10px 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); max-width: 100%; }
.example-empty { font-size: 12px; color: #64748b; background: #f8fafc; border: 1px dashed #cbd5e1; border-left: 4px solid #cbd5e1; border-radius: 10px; padding: 10px 12px; }
.ai-one-line.global { margin-top: 12px; }
/* 防止内容撑破卡片宽度 */
.insight-card { min-width: 0; }
</style>